import { getMiniProgramNativeNavChromePx, isLfMiniProgramEnv } from "./miniProgramEnv";
import { repaintPageShellBg } from "./pageShellBg";

/**
 * 微信 / 抖音当前均为原生导航栏 + webview 在其下（微信 custom 无效；抖音正式包用 default）。
 * 不得再补标题栏高度，否则每页多一截空白。
 */
function shouldSkipMiniProgramTopNavClearance(): boolean {
  return isLfMiniProgramEnv();
}

/** layout 视口底 → 物理屏底的延伸高度（CSS 变量名） */
export const BLEED_BOTTOM_VAR = "--lf-h5-bleed-bottom";
/** 物理屏高度（CSS 变量名，= innerHeight + bleed） */
export const SCREEN_HEIGHT_VAR = "--lf-h5-screen-height";
/**
 * 小程序原生标题栏相对 webview 仍需补的顶部差值（CSS 变量名）。
 * 已在标题栏下方时为 0，不改变现有 safe-area 行为。
 */
export const MINIAPP_TOP_EXTRA_VAR = "--lf-h5-miniapp-top-extra";

function measureSafeAreaBottomPx(): number {
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:fixed;visibility:hidden;pointer-events:none;padding-bottom:env(safe-area-inset-bottom)";
  document.documentElement.appendChild(probe);
  const px = parseFloat(getComputedStyle(probe).paddingBottom) || 0;
  probe.remove();
  return Math.round(px);
}

function measureScreenCssHeightPx(): number {
  const sw = window.screen.width;
  const sh = window.screen.height;
  const portrait = window.innerHeight >= window.innerWidth;
  return portrait ? Math.max(sh, sw) : Math.min(sh, sw);
}

/** 仅 CSS env(safe-area-inset-top)，不含小程序标题栏补差 */
export function measureCssEnvSafeAreaTopPx(): number {
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:fixed;visibility:hidden;pointer-events:none;padding-top:env(safe-area-inset-top)";
  document.documentElement.appendChild(probe);
  const px = parseFloat(getComputedStyle(probe).paddingTop) || 0;
  probe.remove();
  return Math.round(px);
}

/**
 * 小程序原生标题栏底边 → 物理屏顶 的距离（px）。
 * 非小程序为 0。
 */
export function getMiniProgramNativeNavBottomPx(): number {
  if (!isLfMiniProgramEnv()) {
    return 0;
  }
  return Math.max(0, getMiniProgramNativeNavChromePx());
}

/**
 * 估计 webview 顶边相对物理屏顶的偏移。
 * 已在原生标题栏下 ≈ nativeBottom；叠在栏下（如微信 custom）≈ 0。
 */
export function estimateMiniProgramWebviewPhysicalTopPx(): number {
  if (typeof window === "undefined" || !isLfMiniProgramEnv()) {
    return 0;
  }
  const nativeBottom = getMiniProgramNativeNavBottomPx();
  if (nativeBottom <= 0) {
    return 0;
  }
  const bottomSafe = measureSafeAreaBottomPx();
  const totalOutside = Math.max(0, measureScreenCssHeightPx() - window.innerHeight);
  return Math.min(nativeBottom, Math.max(0, totalOutside - bottomSafe));
}

/**
 * 元素顶边相对物理屏顶的距离。
 * = webview 物理顶 + layout top + visualViewport.offsetTop
 */
export function measureElementPhysicalTopPx(el: Element): number {
  const rectTop = el.getBoundingClientRect().top;
  const vvOffset = window.visualViewport?.offsetTop ?? 0;
  if (!isLfMiniProgramEnv()) {
    return rectTop + vvOffset;
  }
  return estimateMiniProgramWebviewPhysicalTopPx() + rectTop + vvOffset;
}

/**
 * 小程序专用：若 H5 顶 < 原生标题栏底，返回需补的 padding，使内容顶对齐到原生标题栏底。
 * 非小程序返回 0（由 env safe-area 路径处理）。
 */
export function calcMiniProgramNativeNavClearancePadPx(el: Element | null): number {
  if (!el || !isLfMiniProgramEnv() || shouldSkipMiniProgramTopNavClearance()) {
    return 0;
  }
  const nativeBottom = getMiniProgramNativeNavBottomPx();
  if (nativeBottom <= 0) {
    return 0;
  }
  const h5Top = measureElementPhysicalTopPx(el);
  if (h5Top >= nativeBottom) {
    return 0;
  }
  return Math.ceil(nativeBottom - h5Top);
}

/**
 * 有效顶部安全距：env(safe-area-inset-top) + 小程序标题栏补差。
 * 非小程序或补差为 0 时与原先 env 一致。
 */
export function measureSafeAreaTopPx(): number {
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:fixed;visibility:hidden;pointer-events:none;padding-top:var(--lf-h5-safe-top, env(safe-area-inset-top))";
  document.documentElement.appendChild(probe);
  const px = parseFloat(getComputedStyle(probe).paddingTop) || 0;
  probe.remove();
  return Math.round(px);
}

/**
 * 计算小程序下「原生标题栏底」相对 webview 顶仍被挡住、且未被 env(safe-area) 覆盖的补差。
 * webview 已在标题栏下 → 0；叠在栏下 → 只补缺口。
 */
export function computeMiniProgramTopExtraInsetPx(): number {
  if (typeof window === "undefined" || !isLfMiniProgramEnv()) {
    return 0;
  }
  if (shouldSkipMiniProgramTopNavClearance()) {
    return 0;
  }
  const nativeBottom = getMiniProgramNativeNavBottomPx();
  if (nativeBottom <= 0) {
    return 0;
  }
  const envTop = measureCssEnvSafeAreaTopPx();
  const webviewTop = estimateMiniProgramWebviewPhysicalTopPx();
  /** webview 内从顶还需让出多少，才能到原生标题栏底 */
  const clearanceInWebview = Math.max(0, nativeBottom - webviewTop);
  return Math.max(0, Math.ceil(clearanceInWebview - envTop));
}

/**
 * 键盘弹起时视口瞬态：此时 screen-innerHeight 会被键盘高度污染，
 * 不能用来重算「原生标题栏是否盖住 webview」。
 */
function isKeyboardTransientViewport(): boolean {
  const vv = window.visualViewport;
  if (vv && vv.offsetTop > 1) {
    return true;
  }
  const active = document.activeElement;
  const focusingEditable =
    active instanceof HTMLElement &&
    (active.tagName === "INPUT" ||
      active.tagName === "TEXTAREA" ||
      active.isContentEditable);
  if (!focusingEditable) {
    return false;
  }
  if (vv && window.innerHeight - vv.height > 80) {
    return true;
  }
  // Android 常见：innerHeight 随键盘缩小，gap 远超顶栏+底安全区
  const gap = Math.max(0, measureScreenCssHeightPx() - window.innerHeight);
  const bottomSafe = measureSafeAreaBottomPx();
  const nativeChrome = isLfMiniProgramEnv() ? getMiniProgramNativeNavChromePx() : 0;
  return gap > nativeChrome + bottomSafe + 60;
}

/** 写入 --lf-h5-miniapp-top-extra；非小程序固定 0。键盘期间保持上次值，避免被误置 0。 */
export function applyMiniProgramTopExtraInset(): void {
  if (typeof document === "undefined") {
    return;
  }
  if (isKeyboardTransientViewport()) {
    return;
  }
  const extra = computeMiniProgramTopExtraInsetPx();
  document.documentElement.style.setProperty(MINIAPP_TOP_EXTRA_VAR, `${extra}px`);
}

function isIosLike(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

/**
 * layout 底到物理屏底需向下延伸的高度。
 * env(safe-area-inset-bottom) 通常只有 Home 条（如 34px），
 * screen.height - innerHeight 才是完整差值（如 62px）。
 */
export function measureBleedBottomPx(): number {
  const fromEnv = measureSafeAreaBottomPx();
  const inner = window.innerHeight;
  const fromScreen = Math.max(0, measureScreenCssHeightPx() - inner);
  if (isIosLike() && fromScreen > 0) {
    return Math.max(fromEnv, fromScreen);
  }
  return fromEnv;
}

function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || "(empty)";
}

function rectLine(label: string, el: Element | null): string[] {
  if (!el) {
    return [`${label}: (not found)`];
  }
  const r = el.getBoundingClientRect();
  const cs = getComputedStyle(el);
  return [
    `${label}.top: ${Math.round(r.top)}`,
    `${label}.bottom: ${Math.round(r.bottom)}`,
    `${label}.height: ${Math.round(r.height)}`,
    `${label}.position: ${cs.position}`,
    `${label}.paddingBottom: ${cs.paddingBottom}`,
    `${label}.gapToInnerBottom: ${Math.round(window.innerHeight - r.bottom)}`,
  ];
}

/** 真机视口 / Tab 诊断报告（供调试弹框复制） */
export function collectViewportDebugReport(): string {
  const vv = window.visualViewport;
  const tabShell = document.querySelector(".space-tab-bar-shell");
  const spaceShell = document.querySelector(".lf-h5-space-shell");
  const html = document.documentElement;
  const root = document.getElementById("root");
  const bleedPx = measureBleedBottomPx();
  const safeBottomPx = measureSafeAreaBottomPx();
  const screenCssH = measureScreenCssHeightPx();
  const lines = [
    `time: ${new Date().toISOString()}`,
    `url: ${window.location.href}`,
    `ua: ${navigator.userAgent}`,
    `iosLike: ${isIosLike()}`,
    `devicePixelRatio: ${window.devicePixelRatio}`,
    `innerWidth: ${window.innerWidth}`,
    `innerHeight: ${window.innerHeight}`,
    `outerWidth: ${window.outerWidth}`,
    `outerHeight: ${window.outerHeight}`,
    `screen.width: ${window.screen.width}`,
    `screen.height: ${window.screen.height}`,
    `screenCssHeight(portrait logic): ${screenCssH}`,
    `screenMinusInner: ${Math.max(0, screenCssH - window.innerHeight)}`,
    `measureSafeAreaTopPx: ${measureSafeAreaTopPx()}`,
    `measureCssEnvSafeAreaTopPx: ${measureCssEnvSafeAreaTopPx()}`,
    `getMiniProgramNativeNavBottomPx: ${getMiniProgramNativeNavBottomPx()}`,
    `estimateMiniProgramWebviewPhysicalTopPx: ${estimateMiniProgramWebviewPhysicalTopPx()}`,
    `computeMiniProgramTopExtraInsetPx: ${computeMiniProgramTopExtraInsetPx()}`,
    `measureSafeAreaBottomPx: ${safeBottomPx}`,
    `measureBleedBottomPx: ${bleedPx}`,
    `css --lf-h5-bleed-bottom: ${cssVar(BLEED_BOTTOM_VAR)}`,
    `css --lf-h5-miniapp-top-extra: ${cssVar(MINIAPP_TOP_EXTRA_VAR)}`,
    `css --lf-h5-screen-height: ${cssVar(SCREEN_HEIGHT_VAR)}`,
    `css --lf-h5-tab-bar-height: ${cssVar("--lf-h5-tab-bar-height")}`,
    `css --lf-h5-tab-bar-content-height: ${cssVar("--lf-h5-tab-bar-content-height")}`,
    `html.clientHeight: ${html.clientHeight}`,
    `html.offsetHeight: ${html.offsetHeight}`,
    `html.style.height: ${html.style.height || "(inline empty)"}`,
    `html.computed.height: ${getComputedStyle(html).height}`,
    `body.clientHeight: ${document.body.clientHeight}`,
    `#root.clientHeight: ${root?.clientHeight ?? "(missing)"}`,
    ...rectLine("spaceShell", spaceShell),
    ...rectLine("tabShell", tabShell),
  ];

  if (vv) {
    lines.push(
      `visualViewport.width: ${Math.round(vv.width)}`,
      `visualViewport.height: ${Math.round(vv.height)}`,
      `visualViewport.offsetTop: ${Math.round(vv.offsetTop)}`,
      `visualViewport.offsetLeft: ${Math.round(vv.offsetLeft)}`,
      `visualViewport.scale: ${vv.scale}`
    );
  } else {
    lines.push("visualViewport: (unsupported)");
  }

  return lines.join("\n");
}

let lockedScreenHeightPx: number | null = null;
let viewportFillSuppressed = false;

/** 测试页等场景：键盘期间禁止 applyViewportFill 改 document 高度 */
export function setViewportFillSuppressed(suppressed: boolean): void {
  viewportFillSuppressed = suppressed;
}

export function isViewportFillSuppressed(): boolean {
  return viewportFillSuppressed;
}

export function lockViewportHeight(px?: number): void {
  const bleed = measureBleedBottomPx();
  lockedScreenHeightPx = px ?? window.innerHeight + bleed;
  applyViewportFill();
}

export function unlockViewportHeight(): void {
  lockedScreenHeightPx = null;
  applyViewportFill();
}

/** 写入物理屏高度到 html；iOS fixed 层绘制不跟 box 高度走，须用文档流 */
export function applyViewportFill(): void {
  if (viewportFillSuppressed) {
    return;
  }
  // 键盘瞬态勿改 document 高度 / top-extra，否则顶栏补差会被算成 0 且收起后难复位
  if (isKeyboardTransientViewport()) {
    return;
  }
  applyMiniProgramTopExtraInset();
  const bleed = measureBleedBottomPx();
  const screenHeight = lockedScreenHeightPx ?? window.innerHeight + bleed;
  const doc = document.documentElement;
  const px = `${screenHeight}px`;
  doc.style.setProperty(BLEED_BOTTOM_VAR, `${bleed}px`);
  doc.style.setProperty(SCREEN_HEIGHT_VAR, px);
  doc.style.height = px;
  const body = document.body;
  if (body) {
    body.style.height = px;
    body.style.minHeight = px;
  }
  repaintPageShellBg();
}

if (typeof window !== "undefined") {
  window.__lfH5ApplyViewport = applyViewportFill;
}

function recoverDocumentScrollAfterKeyboard(): void {
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

export function initViewportFill(): () => void {
  applyViewportFill();
  let prevVvHeight = window.visualViewport?.height ?? window.innerHeight;
  const refresh = () => {
    applyViewportFill();
    const vv = window.visualViewport;
    const nextH = vv?.height ?? window.innerHeight;
    // 键盘收起：高度明显回升时强制滚回顶，避免内置顶栏卡在上移位置
    if (nextH > prevVvHeight + 40) {
      recoverDocumentScrollAfterKeyboard();
      window.requestAnimationFrame(recoverDocumentScrollAfterKeyboard);
    }
    prevVvHeight = nextH;
  };
  const onVvScroll = () => {
    // offsetTop 归零时补一次顶栏相关 CSS（键盘期间可能跳过了 top-extra 写入）
    if ((window.visualViewport?.offsetTop ?? 0) <= 1) {
      applyViewportFill();
    }
  };
  window.addEventListener("resize", refresh);
  window.addEventListener("orientationchange", refresh);
  window.visualViewport?.addEventListener("resize", refresh);
  window.visualViewport?.addEventListener("scroll", onVvScroll);
  return () => {
    window.removeEventListener("resize", refresh);
    window.removeEventListener("orientationchange", refresh);
    window.visualViewport?.removeEventListener("resize", refresh);
    window.visualViewport?.removeEventListener("scroll", onVvScroll);
  };
}
